import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Flame, Gauge, Target } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import { API, trackEvent, formatApiError } from "@/lib/api";

export default function WelcomeBack() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    document.title = "Welcome Back — Wired 2 Win Golf";
    trackEvent("page_view", { page: "welcome-back" });
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email, source: "welcome-back" });
      setStatus("done");
      setMessage(data.message);
      setEmail("");
      trackEvent("subscribed", { source: "welcome-back" });
    } catch (err) {
      setStatus("error");
      setMessage(formatApiError(err));
    }
  };

  return (
    <main data-testid="welcome-back-page" className="pt-32 lg:pt-40 min-h-[100svh]">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <Overline className="mb-6"><MaskedLine>Assessment complete. Now the work begins.</MaskedLine></Overline>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-7xl uppercase max-w-4xl">
          <MaskedLine delay={0.15}>You know how</MaskedLine>
          <MaskedLine delay={0.3}>you're wired.</MaskedLine>
          <MaskedLine delay={0.45}><span className="text-volt">Now train it.</span></MaskedLine>
        </h1>
        <Reveal delay={0.6}>
          <p className="mt-8 max-w-xl text-white/60 text-base sm:text-lg leading-relaxed">
            Your report just mapped your Primary and Accent wiring — what drives you, the conditions you need, and your Mental Mulligan for when pressure hits. Every week, we send one sharp drill built on the same system to help you train it.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <Reveal>
          <div className="border border-volt/40 bg-volt/5 p-8 sm:p-12 max-w-2xl" data-testid="welcome-back-capture">
            {status === "done" ? (
              <div data-testid="welcome-back-success" className="flex items-start gap-4">
                <span className="w-12 h-12 bg-volt flex items-center justify-center shrink-0"><Check className="w-6 h-6 text-obsidian" /></span>
                <div>
                  <h2 className="font-display font-bold text-2xl tracking-tight">{message}</h2>
                  <p className="text-white/50 text-sm mt-2">First drill lands this week. Meanwhile, dig into the resources below.</p>
                </div>
              </div>
            ) : (
              <>
                <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-2">Get the weekly wiring drill</h2>
                <p className="text-white/50 text-sm mb-6">Free. One email a week. Unsubscribe anytime.</p>
                <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3" data-testid="welcome-back-form">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    data-testid="welcome-back-email-input"
                    aria-label="Email address"
                    className="flex-1 bg-white/5 border border-white/15 px-4 py-3.5 text-sm placeholder:text-white/30 focus:outline-none focus:border-volt transition-colors"
                  />
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="welcome-back-subscribe-button"
                    className="bg-volt text-obsidian font-display font-bold px-6 py-3.5 hover:bg-white transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {status === "loading" ? "Wiring you in..." : "Wire Me In"} <ArrowRight className="w-4 h-4" />
                  </motion.button>
                </form>
                {status === "error" && <p data-testid="welcome-back-error" className="text-red-400 text-xs mt-3">{message}</p>}
              </>
            )}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14 max-w-4xl">
          {[
            { icon: Target, t: "One drill, weekly", d: "A single rep for your mental game — never a firehose.", id: "wb-drill" },
            { icon: Gauge, t: "Same system", d: "Built on the G-O-L-F model behind your assessment.", id: "wb-system" },
            { icon: Flame, t: "Course-ready", d: "Everything is usable on your very next round.", id: "wb-ready" },
          ].map((b, i) => (
            <Reveal key={b.id} delay={i * 0.1}>
              <div data-testid={b.id} className="border border-white/10 bg-ink p-6 h-full">
                <b.icon className="w-6 h-6 text-volt mb-4" />
                <h3 className="font-display text-lg font-bold tracking-tight mb-2">{b.t}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-14">
          <Link
            to="/resources"
            data-testid="welcome-back-resources-cta"
            onClick={() => trackEvent("cta_click", { location: "welcome-back" })}
            className="group inline-flex items-center gap-2 text-volt font-bold text-sm hover:text-white transition-colors"
          >
            Browse the training resources <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
}
